import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Search, RefreshCw, X, Check, LayoutTemplate, Send, FlaskConical, Loader2, Pencil, Eye, Target, Zap, ThumbsUp, HelpCircle, MapPin, Wand2, Plus, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { fillPlaceholders, buildContext } from "@/lib/placeholders";
import { api, apiErrorMessage } from "@/lib/api";
import { BOARD_TYPES } from "@/pages/cotizador/shared";

// Meta de badges de calidad del match plantilla↔reserva (backend devuelve `quality`).
/**
 * Extrae campos estructurados TD de una descripción HTML (sales_description
 * de KimKim / Espíritu). Devuelve solo los campos detectados; los no
 * detectados se omiten para no sobrescribir datos ya introducidos.
 */
/**
 * Compone el HTML de sales_description con formato ordenado a partir de los
 * campos estructurados TD. El resultado se puede guardar de vuelta en la
 * plantilla para migrar el TD viejo (párrafo grande) al nuevo formato limpio.
 */
// Bloque de campos TD auto-generado: se envuelve con un marcador para poder regenerarlo sin pisar
// el texto de la plantilla (bug nov-2026: «Guardar» borraba la descripción elegida).
const TD_FIELDS_OPEN = '<div data-td-fields="1">';
const TD_FIELDS_CLOSE = "</div>";
const wrapTDFields = (html) => (html ? `${TD_FIELDS_OPEN}\n${html}\n${TD_FIELDS_CLOSE}` : "");
const FIELD_LINE_RX = /<p><strong>[^<]{1,60}<\/strong>\s*(?:<a [^>]*>[^<]*<\/a>|[^<]*)<\/p>|<p><strong>(?:Private|Small Group)<\/strong><\/p>|<p><strong>(?:Advises|Room types?):<\/strong><\/p><ul>[\s\S]*?<\/ul>/g;
const GENERATED_LABELS = "Meeting point|Drop off|Starting time|Duration|Guide|Contact phone|Includes|Booking ID|Check-in|Check-out|Address|Board|Reservation under the name of|Room types?|Advises";
// Línea generada por composeTDDescriptionFromFields (label en <strong>, valor fuera). Las plantillas
// escriben «<strong>Meeting Point: X</strong>» (todo dentro), así que no coinciden.
const GENERATED_LINE_RX = new RegExp(
  `^\\s*(?:<p><strong>(?:${GENERATED_LABELS}):<\\/strong>\\s*(?:<a [^>]*>[^<]*<\\/a>|[^<]*)<\\/p>|<p><strong>(?:Private|Small Group)<\\/strong><\\/p>|<p><strong>(?:Advises|Room types?):<\\/strong><\\/p><ul>[\\s\\S]*?<\\/ul>)`,
  "i"
);

/** Texto de la descripción SIN el bloque de campos (marcado o legacy solo-campos). */
export function stripTDFieldsBlock(html) {
  let body = String(html || "");
  const start = body.indexOf(TD_FIELDS_OPEN);
  if (start >= 0) {
    const end = body.indexOf(TD_FIELDS_CLOSE, start);
    if (end >= 0) body = body.slice(0, start) + body.slice(end + TD_FIELDS_CLOSE.length);
  } else if (body.trim() && !body.replace(FIELD_LINE_RX, "").replace(/\s|<br\s*\/?>/g, "").trim()) {
    body = "";  // legacy: la descripción eran solo líneas de campos → se regenera entera
  } else {
    // Sin marcador (p. ej. el editor lo perdió): quitamos las líneas de campos generadas al inicio.
    let m;
    while ((m = GENERATED_LINE_RX.exec(body))) body = body.slice(m[0].length);
  }
  return body.trim();
}

/** Campos regenerados + texto de la plantilla/libre, sin duplicar el bloque anterior. */
export function mergeTDDescription(fieldsHtml, existingHtml) {
  const body = stripTDFieldsBlock(existingHtml);
  const block = wrapTDFields(body ? dedupeNarrative(fieldsHtml, body) : fieldsHtml);
  return [block, body].filter(Boolean).join("\n");
}

const plainText = (html) => String(html || "").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();

// Los campos (Description, Advises, Includes, Meeting point…) suelen venir extraídos del propio texto
// de la plantilla: si ese texto ya está en el cuerpo, no se repiten en el bloque de campos.
function dedupeNarrative(fieldsHtml, bodyHtml) {
  const body = plainText(bodyHtml);
  const inBody = (html) => { const t = plainText(html); return t.length >= 25 && body.includes(t.slice(0, 120)); };
  let out = String(fieldsHtml || "");
  out = out.replace(/<ul>([\s\S]*?)<\/ul>/g, (ul, inner) => {
    const kept = (inner.match(/<li>[\s\S]*?<\/li>/g) || []).filter((li) => !inBody(li));
    return kept.length ? `<ul>${kept.join("")}</ul>` : "";
  });
  out = out.replace(/<p><strong>(?:Advises|Room types?):<\/strong><\/p>(?!<ul>)/g, "");
  out = out.replace(/<p>[\s\S]*?<\/p>/g, (p) => (inBody(p) ? "" : p));
  return out.split("\n").filter((l) => l.trim()).join("\n");
}

export function composeTDDescriptionFromFields(r) {
  const parts = [];
  // Transfers y trenes no llevan Meeting Point (Ventas, oct-2026).
  const mp = ["transfer", "tren"].includes(r.type) ? "" : (r.td_meeting_point || "").trim();
  const mpUrl = (r.td_meeting_point_url || "").trim();
  if (mp) {
    const link = mpUrl ? `<a href="${mpUrl}" target="_blank" rel="noopener noreferrer">${mp}</a>` : mp;
    parts.push(`<p><strong>Meeting point:</strong> ${link}</p>`);
  }
  // Drop Off — solo tiene sentido en transfers (aunque si el operador lo
  // rellena en otro tipo tampoco pasa nada, lo renderizamos).
  const dropOff = (r.td_drop_off || "").trim();
  const dropOffUrl = (r.td_drop_off_url || "").trim();
  if (dropOff) {
    const linkOff = dropOffUrl ? `<a href="${dropOffUrl}" target="_blank" rel="noopener noreferrer">${dropOff}</a>` : dropOff;
    parts.push(`<p><strong>Drop off:</strong> ${linkOff}</p>`);
  }
  if (r.td_starting_time) parts.push(`<p><strong>Starting time:</strong> ${r.td_starting_time}</p>`);
  if (r.td_duration) parts.push(`<p><strong>Duration:</strong> ${r.td_duration}</p>`);
  if (r.td_guide_name) parts.push(`<p><strong>Guide:</strong> ${r.td_guide_name}</p>`);
  if (r.td_contact_phone) parts.push(`<p><strong>Contact phone:</strong> ${r.td_contact_phone}</p>`);
  if (r.group_type) {
    const label = r.group_type === "small_group" ? "Small Group" : "Private";
    parts.push(`<p><strong>${label}</strong></p>`);
  }
  if (r.td_advises) {
    const bullets = r.td_advises.split(/\n+/).filter(Boolean).map((a) => `<li>${a}</li>`).join("");
    parts.push(`<p><strong>Advises:</strong></p><ul>${bullets}</ul>`);
  }
  if (r.td_description) {
    const paragraphs = r.td_description.split(/\n\n+/).filter(Boolean).map((p) => `<p>${p}</p>`).join("");
    parts.push(paragraphs);
  }
  if (r.td_included) parts.push(`<p><strong>Includes:</strong> ${r.td_included}</p>`);
  if (r.confirmation_number) parts.push(`<p><strong>Booking ID:</strong> ${r.confirmation_number}</p>`);
  return parts.join("\n");
}

function extractTDFieldsFromDescription(html) {
  if (!html) return {};
  const out = {};
  // 1) Meeting Point — variantes:
  //    a) <strong>Meeting point:</strong> <a href="google.com/maps/...">TEXTO</a>
  //    b) <strong>Meeting point:</strong> TEXTO PLANO
  //    c) <strong>Meeting point: <a href="google.com/maps/...">TEXTO</a>. </strong> (label + link envueltos en <strong>)
  //    Extraemos: dirección legible + URL Google Maps (si viene).
  const mpBlockRx = /Meeting\s*point[:\s]*([\s\S]{0,600}?)(?=<\/p>|<p |$)/i;
  const mpBlock = html.match(mpBlockRx);
  if (mpBlock) {
    const chunk = mpBlock[1];
    // URL Google Maps (opcional)
    const urlM = chunk.match(/href="([^"]*google\.[^/]+\/maps[^"]*)"/i);
    if (urlM) out.td_meeting_point_url = urlM[1];
    // Texto legible: quitamos etiquetas y limpiamos
    const plain = chunk.replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
    // Corte inteligente: cortamos al primer separador de frase real, evitando
    // partir abreviaturas como "Av.", "Ave.", "St.", "Nº." típicas de direcciones.
    // Prioridades:
    //   a) " . "  (espacio-punto-espacio) → siempre es final de frase
    //   b) ". "  precedido de PALABRA + espacio (no abreviatura conocida)
    //   c) tope 200 chars
    let text = plain;
    const strongDot = text.indexOf(" . ");
    if (strongDot >= 6) {
      text = text.slice(0, strongDot);
    } else {
      // Buscar ". " que NO forme parte de una abreviatura común
      const ABBR = /(Av|Ave|Avda|Blvd|Bvd|Sr|Sra|Sres|Dr|Nº|N|St|Pl|Pza|Pça|Praca|Etc|Rd)\.$/i;
      const rx = /\.\s+[A-ZÁÉÍÓÚÑ]/g;
      let m;
      while ((m = rx.exec(text))) {
        const before = text.slice(0, m.index + 1);
        const lastToken = before.match(/(\S+)\.$/);
        if (lastToken && ABBR.test(lastToken[0])) continue;
        if (m.index >= 6 && m.index <= 200) { text = before.slice(0, -1); break; }
      }
    }
    text = text.replace(/[.:,·\s]+$/, "").trim();
    if (text) out.td_meeting_point = text;
  }
  // 2) Guide / Host / Driver ────────────────────────────────────────────
  const guideRx = /<strong>\s*(?:Guide|Host(?:name)?|Driver)[:\s]*<\/strong>\s*([\s\S]{0,120}?)(?:<\/p>|<br)/i;
  const gm = html.match(guideRx);
  if (gm) {
    const raw = gm[1].replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    const phRx = /(\+?\d[\d\s().-]{7,}\d)/;
    const phM = raw.match(phRx);
    if (phM) {
      out.td_contact_phone = phM[1].replace(/\s+/g, " ").trim();
      const nameOnly = raw.replace(phRx, "").replace(/[·:,\s]+$/, "").replace(/^[·:,\s]+/, "").trim();
      if (nameOnly && !/updated .*before/i.test(nameOnly)) out.td_guide_name = nameOnly;
    } else if (raw && !/updated .*before|will be updated/i.test(raw)) {
      out.td_guide_name = raw.replace(/[·:,\s]+$/, "").trim();
    }
  }
  // 3) Teléfono suelto en el body ────────────────────────────────────────
  if (!out.td_contact_phone) {
    const anyPhRx = /(?:call|contact|phone|tel[:.]?|teléfono|telefono)[^\d+]{0,15}(\+?\d[\d\s().-]{7,}\d)/i;
    const pm = html.match(anyPhRx);
    if (pm) out.td_contact_phone = pm[1].replace(/\s+/g, " ").trim();
  }
  // 4) Included ──────────────────────────────────────────────────────────
  const incRx = /<strong>\s*(?:Includes?|Included)[:\s]*<\/strong>([\s\S]{0,400}?)(?:<\/p>|<\/li>\s*<\/ul>|<strong)/i;
  const im = html.match(incRx);
  if (im) {
    const raw = im[1].replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (raw) out.td_included = raw.replace(/[.:,·\s]+$/, "");
  }
  // 5) Advises · frases en <strong> que empiezan con verbos de recomendación
  //    o preposiciones típicas de aviso ("Please …", "In case …", "Your guide will …",
  //    "Bring …", "Wear …", "Note …"). Ignoramos las líneas internas "CHECK …" del
  //    operador. Recopilamos hasta 3 frases y las juntamos con salto de línea.
  const stripInlineAdv = (chunk) => chunk
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/[ \t]+/g, " ")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  const advises = [];
  const strongParas = [...html.matchAll(/<p[^>]*>\s*(?:<strong>([\s\S]*?)<\/strong>)\s*<\/p>/gi)];
  const ADVISE_HINTS = /^(please|in case|your guide|note|important|bring|wear|arrive|make sure|remember|it['']s|it is|the guide|dress|be there)/i;
  const CHECK_NOTE = /(CHECK|test|todo|revisar|check .*MP|check .*MEETING)/i;
  const alreadyGuide = out.td_guide_name || "";
  for (const s of strongParas) {
    const txt = stripInlineAdv(s[1] || "");
    if (!txt || txt.length < 15 || txt.length > 400) continue;
    if (CHECK_NOTE.test(txt)) continue;
    if (/^Meeting\s*point/i.test(txt)) continue;
    if (/^(Guide|Host|Driver|Includes?|Duration)[:\s]/i.test(txt)) continue;
    if (alreadyGuide && txt.includes(alreadyGuide)) continue;
    if (!ADVISE_HINTS.test(txt)) continue;
    advises.push(txt);
    if (advises.length >= 3) break;
  }
  if (advises.length) out.td_advises = advises.join("\n\n");
  // 6) Description · texto narrativo (párrafos "isSelectedEnd" o texto plano
  //    después de las meta-líneas). Se preservan los saltos de párrafo (<p> →
  //    dobles \n) y los saltos de línea inline (<br> → \n) para no perder la
  //    estructura visual. Se limita a ~1500 chars.
  const stripInline = (chunk) => chunk
    .replace(/<br\s*\/?>/gi, "\n")               // <br> → \n
    .replace(/<[^>]+>/g, "")                     // quita el resto de tags
    .replace(/&nbsp;/g, " ")
    .replace(/[ \t]+/g, " ")                     // colapsa espacios/tabs (NO \n)
    .replace(/\n[ \t]+/g, "\n")                  // limpia espacios al inicio de línea
    .replace(/[ \t]+\n/g, "\n")                  // limpia espacios al final de línea
    .replace(/\n{3,}/g, "\n\n")                  // máximo 2 saltos seguidos
    .trim();
  const bodyParas = [...html.matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi)]
    .map((m) => stripInline(m[1]))
    .filter((t) => {
      if (t.length < 60) return false;
      if (/Meeting point|Guide|Host|Includes?|CHECK|In case of/i.test(t)) return false;
      if (ADVISE_HINTS.test(t)) return false;
      return true;
    });
  if (bodyParas.length) {
    out.td_description = bodyParas.slice(0, 4).join("\n\n").slice(0, 1500);
  }
  return out;
}

const QUALITY_META = {
  perfect: { label: "Coincidencia perfecta", icon: Target, cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  great:   { label: "Muy buena",             icon: Zap,      cls: "bg-teal-100 text-teal-800 border-teal-300" },
  good:    { label: "Sugerida",              icon: ThumbsUp, cls: "bg-blue-100 text-blue-800 border-blue-300" },
  weak:    { label: "Posible",               icon: HelpCircle, cls: "bg-slate-100 text-slate-700 border-slate-300" },
};

/**
 * Campos estructurados de TD para tipo=actividad. Autopoblan desde la reserva
 * y marcan en rojo los que quedan vacíos. Meeting Point auto-genera link de
 * Google Maps a partir de la dirección o del nombre del punto de encuentro.
 */
/**
 * Compone el HTML de sales_description para ALOJAMIENTO. Formato limpio en
 * inglés a partir de campos estructurados (check-in/out, address, room, board,
 * reservation name).
 */
export function composeTDDescriptionFromFieldsAloj(r) {
  const parts = [];
  const addr = (r.hotel_address || r.td_meeting_point || "").trim();
  const url = (r.td_hotel_map_url || "").trim();
  if (r.date_entry) parts.push(`<p><strong>Check-in:</strong> ${r.date_entry}</p>`);
  if (r.date_exit) parts.push(`<p><strong>Check-out:</strong> ${r.date_exit}</p>`);
  if (addr) {
    const link = url ? `<a href="${url}" target="_blank" rel="noopener noreferrer">${addr}</a>` : addr;
    parts.push(`<p><strong>Address:</strong> ${link}</p>`);
  }
  if (r.room) {
    // Una línea por habitación (2026-07 · Eduardo): replica la estructura
    // "arriba" (Ficha del hotel → Tipos de habitación) donde cada grupo
    // { count, type_text } se muestra en su propia fila. Ej. "2 Suites · 1 Twin"
    // → dos <li>: "2 × Suites" y "1 × Twin". Fallback: si el string no se
    // puede partir en filas, se muestra como línea única (compat).
    const chunks = String(r.room)
      .split(/\s*[·+,]\s*/)
      .map((s) => s.trim())
      .filter(Boolean);
    const lines = chunks
      .map((chunk) => {
        // Acepta "2 Suites", "1x Double", "3 × Twin" — variantes comunes.
        const m = chunk.match(/^(\d+)\s*[x×]?\s+(.+)$/i);
        return m
          ? { count: parseInt(m[1], 10) || 1, type_text: m[2].trim() }
          : { count: 1, type_text: chunk };
      });
    const label = lines.length > 1 ? "Room types" : "Room type";
    if (lines.length > 1) {
      const items = lines.map((l) => `<li>${l.count} × ${l.type_text}</li>`).join("");
      parts.push(`<p><strong>${label}:</strong></p><ul>${items}</ul>`);
    } else if (lines.length === 1) {
      const l = lines[0];
      parts.push(`<p><strong>${label}:</strong> ${l.count} × ${l.type_text}</p>`);
    }
  }
  if (r.board) {
    // La BD usa valores en español: sa/bb/mp/pc. Traducimos a inglés para el TD.
    const BOARD_MAP = { sa: "Room Only", bb: "Bed & Breakfast", mp: "Half Board", pc: "Full Board" };
    parts.push(`<p><strong>Board:</strong> ${BOARD_MAP[r.board] || r.board}</p>`);
  }
  if (r.td_reservation_name) parts.push(`<p><strong>Reservation under the name of:</strong> ${r.td_reservation_name}</p>`);
  if (r.confirmation_number) parts.push(`<p><strong>Booking ID:</strong> ${r.confirmation_number}</p>`);
  if (r.td_description) {
    // Description libre: preservamos saltos de párrafo (\n\n) del textarea.
    const paragraphs = r.td_description.split(/\n\n+/).filter(Boolean).map((p) => `<p>${p}</p>`).join("");
    if (paragraphs) parts.push(paragraphs);
  }
  if (r.td_advises) {
    parts.push(`<p><strong>Advises:</strong> ${r.td_advises.replace(/\n/g, "<br>")}</p>`);
  }
  return parts.join("\n");
}

/**
 * Campos estructurados de TD para tipo=alojamiento. Labels en inglés (van
 * al viajero). Auto-genera link de Google Maps desde la dirección del hotel.
 * Rojo si un campo obligatorio está vacío.
 */
function TDFieldsAlojamiento({ reserva, trip, onChange }) {
  // Autopoblación inicial:
  //   · reservation name ← main_traveler (si vacío)
  //   · hotel_name ← service (si vacío) → así la ficha del hotel de arriba
  //     arranca con algo aunque el operador aún no haya seleccionado del directorio
  //   · board ← "bb" (Bed & Breakfast) si vacío. Es el régimen más común y
  //     evita que TD Fields muestre "-- Select --" cuando la reserva antigua
  //     no tenía board persistido en BD.
  React.useEffect(() => {
    const patch = {};
    // Titular real (traveler con client=true) — cae al título del itinerario
    // (`main_traveler`) sólo como último recurso. Eduardo (2026-07): el
    // reservation name que va al hotel debe ser SIEMPRE el nombre del viajero,
    // nunca el título con versiones tipo "Salvatore Vella 2.0 (v5 - booked)".
    const titular = (trip?.titular_name || trip?.main_traveler || "").trim();
    if (!reserva.td_reservation_name && titular) patch.td_reservation_name = titular;
    if (!reserva.hotel_name && reserva.service) patch.hotel_name = reserva.service;
    if (!reserva.board) patch.board = "bb";
    if (Object.keys(patch).length) onChange(patch);
  }, [reserva.id]);

  const set = (k, v) => onChange({ [k]: v });
  const gmapsUrl = (q) => q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : "";
  const addressForMap = reserva.hotel_address || "";
  const mapsUrl = reserva.td_hotel_map_url || gmapsUrl(addressForMap);

  const runReformat = () => {
    const html = composeTDDescriptionFromFieldsAloj(reserva);
    if (!html.trim()) {
      toast.info("Antes de reformatear, rellena al menos un campo (dirección, habitación, etc.).");
      return;
    }
    onChange({ sales_description: mergeTDDescription(html, reserva.sales_description) });
    toast.success("Descripción de alojamiento reformateada · recuerda guardar la reserva");
  };

  // Reutilizamos BOARD_TYPES globales (mismos values sa/bb/mp/pc y mismas
  // labels EN que usa la fila principal "Board" para consistencia total).
  const fields = [
    { key: "date_entry",       label: "Check-in",       type: "date",     required: true },
    { key: "date_exit",        label: "Check-out",      type: "date",     required: true },
    { key: "hotel_address",    label: "Address",        type: "address",  required: true, placeholder: "Full hotel address (street, city, country)" },
    { key: "room",             label: "Room type(s)",   type: "text",     required: true, placeholder: "Ej. 1 Double Deluxe + 1 Twin" },
    { key: "board",            label: "Board",          type: "select",   required: true, options: BOARD_TYPES },
    { key: "td_reservation_name", label: "Reservation name", type: "text", required: true, placeholder: "Ej. Salvatore Amato" },
    { key: "confirmation_number", label: "Booking ID", type: "text", optional: true, placeholder: "Ej. 9730205465" },
    { key: "td_description",   label: "Description",    type: "textarea", optional: true, placeholder: "Descripción del hotel para el viajero (amenities, notas, tips…)" },
    { key: "td_advises",       label: "Advises",        type: "textarea", optional: true, placeholder: "Consejos: horario check-in, dress code, restricciones, etc." },
  ];

  return (
    <div className="rounded-lg border bg-card p-4" data-testid="td-fields-alojamiento">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-primary" />
          <h3 className="font-heading text-sm font-bold">Travel Document Fields · Accommodation</h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 gap-1 border-emerald-400/60 bg-emerald-50/50 text-xs text-emerald-800 hover:bg-emerald-100"
            onClick={runReformat}
            data-testid="td-reformat-btn-aloj"
          >
            <RefreshCw className="h-3 w-3" />Guardar como formato nuevo
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {fields.map((f) => {
          const value = reserva[f.key] || "";
          const empty = f.required && !value.toString().trim();
          const commonCls = `text-sm ${empty ? "border-rose-400 bg-rose-50/40 placeholder:text-rose-400" : ""}`;
          return (
            <div key={f.key} className={(f.key === "hotel_address" || f.type === "textarea") ? "sm:col-span-2" : ""}>
              <label className={`mb-1 block text-[10px] font-semibold uppercase tracking-wider ${empty ? "text-rose-600" : "text-muted-foreground"}`}>
                {f.label} {empty && <span className="ml-0.5">*</span>}
              </label>
              {f.type === "select" ? (
                <select
                  className={`h-9 w-full rounded-md border bg-transparent px-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${commonCls}`}
                  value={value}
                  onChange={(e) => set(f.key, e.target.value)}
                  data-testid={`td-aloj-${f.key}`}
                >
                  <option value="">— Select —</option>
                  {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : f.type === "date" ? (
                <Input type="date" value={value} onChange={(e) => set(f.key, e.target.value)} className={`h-9 ${commonCls}`} data-testid={`td-aloj-${f.key}`} />
              ) : f.type === "textarea" ? (
                <textarea
                  className={`w-full rounded-md border px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${commonCls}`}
                  rows={f.key === "td_description" ? 3 : 2}
                  value={value}
                  onChange={(e) => set(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  data-testid={`td-aloj-${f.key}`}
                />
              ) : (
                <Input value={value} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder} className={`h-9 ${commonCls}`} data-testid={`td-aloj-${f.key}`} />
              )}
              {f.key === "hotel_address" && value && (
                <div className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <MapPin className="h-3 w-3 text-blue-600" />
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 underline decoration-blue-400/50 underline-offset-2 hover:text-blue-900"
                    data-testid="td-aloj-maps-link"
                    title="Abre Google Maps con esta dirección en una pestaña nueva"
                  >
                    Abrir en Google Maps
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const TD_ACTIVIDAD_FIELDS = [
  { key: "td_starting_time",  label: "Starting Time",   type: "text",     autofillFrom: "hour_entry",   placeholder: "Ej. 09:00" },
  { key: "td_duration",       label: "Duration",        type: "text",     placeholder: "Ej. 3h · half-day" },
  { key: "td_meeting_point",  label: "Meeting Point",   type: "text",     excludeTypes: ["transfer", "tren"], placeholder: "Dirección o hotel de recogida" },
  { key: "td_drop_off",       label: "Drop Off",        type: "text",     onlyType: "transfer",         placeholder: "Destino del transfer (auto desde el título 'X to Y')" },
  { key: "td_contact_phone",  label: "Contact Phone",   type: "text",     placeholder: "+34 …" },
  { key: "td_guide_name",     label: "Guide Name",      type: "text",     placeholder: "Ej. Carlos" },
  { key: "group_type",        label: "Small Group / Private", type: "select", excludeType: "transfer",
    options: [ { value: "private", label: "Private" }, { value: "small_group", label: "Small Group" } ],
    placeholder: "Selecciona modalidad" },
  { key: "confirmation_number", label: "Booking ID", type: "text", optional: true, placeholder: "Ej. 9730205465" },
  { key: "td_advises",        label: "Advises",         type: "textarea", placeholder: "Consejos y recomendaciones para el viajero" },
  { key: "td_description",    label: "Description",     type: "textarea", placeholder: "Descripción para el viajero" },
  { key: "td_included",       label: "Included",        type: "textarea", placeholder: "Qué incluye la actividad" },
];

// Extrae Meeting Point + Drop Off del título del servicio para transfers.
// Ejemplo: "Private Transfer Piazza di Spagna 9 to Villa in Margole 12, Le Margole, Radda in Chianti"
//   → Meeting Point = "Piazza di Spagna 9"
//   → Drop Off      = "Villa in Margole 12, Le Margole, Radda in Chianti"
// La regex tolera prefijos "Private/Shared/Group Transfer" y el conector " to ".
function extractTransferEndpoints(serviceTitle) {
  if (!serviceTitle) return {};
  // Quita prefijo "Private/Shared/Group/Airport Transfer" del principio (case-insensitive).
  const cleaned = serviceTitle.replace(/^\s*(private|shared|group|airport|luxury)?\s*transfer\s+/i, "").trim();
  // Divide por " to " (case-insensitive), respetando la primera ocurrencia.
  const m = cleaned.match(/^(.+?)\s+to\s+(.+)$/i);
  if (!m) return {};
  return { meeting: m[1].trim(), dropoff: m[2].trim() };
}

function TDFieldsActividad({ reserva, trip, onChange }) {
  // Query lazy del proveedor para obtener teléfono/email/address (solo si hay provider_id)
  const provQ = useQuery({
    queryKey: ["provider-td", reserva.provider_id],
    queryFn: async () => (await api.get(`/providers/${reserva.provider_id}`)).data,
    enabled: !!reserva.provider_id,
    staleTime: 5 * 60_000,
  });

  // Auto-populación proactiva al abrir el tab Template:
  //   · td_starting_time  ← hour_entry
  //   · td_meeting_point  ← start_location (transfers KimKim) o dirección del proveedor
  //   · td_contact_phone  ← telefono del proveedor
  //   · Si hay sales_description → ejecutar extractor para rellenar **solo los
  //     campos TD que sigan vacíos** (respeta lo ya editado por el operador).
  React.useEffect(() => {
    const patch = {};
    if (!reserva.td_starting_time && reserva.hour_entry) patch.td_starting_time = reserva.hour_entry;
    // Transfers: extraemos Meeting Point + Drop Off del título del servicio
    // (`Private Transfer X to Y`). Solo si los campos aún están vacíos —
    // así no pisamos ediciones manuales del operador.
    if (reserva.type === "transfer") {
      const ep = extractTransferEndpoints(reserva.service);
      if (ep.dropoff && !reserva.td_drop_off) patch.td_drop_off = ep.dropoff;
    }
    const noMeeting = ["transfer", "tren"].includes(reserva.type);
    if (!noMeeting && !reserva.td_meeting_point && !patch.td_meeting_point) {
      if (reserva.start_location) patch.td_meeting_point = reserva.start_location;
      else if (provQ.data?.direccion || provQ.data?.address) patch.td_meeting_point = provQ.data.direccion || provQ.data.address;
    }
    if (!reserva.td_contact_phone) {
      const phone = provQ.data?.telefono_reservas || provQ.data?.phone || provQ.data?.telefono;
      if (phone) patch.td_contact_phone = phone;
    }
    // Extractor sobre sales_description → SOLO rellena campos TD que siguen
    // vacíos. Antes exigía que TODOS estuvieran vacíos (bloqueaba la auto-
    // extracción cuando `td_starting_time` ya se copiaba de `hour_entry`).
    const hasDesc = ((reserva.sales_description || "").length) > 80;
    if (hasDesc) {
      const extracted = extractTDFieldsFromDescription(reserva.sales_description);
      Object.entries(extracted).forEach(([k, v]) => {
        // Solo aplica si el valor extraído tiene contenido y el campo actual
        // (tanto en `reserva` como en el patch pendiente) sigue vacío.
        const currentInReserva = (reserva[k] || "").toString().trim();
        const currentInPatch = (patch[k] || "").toString().trim();
        if (v && !currentInReserva && !currentInPatch) patch[k] = v;
      });
    }
    if (Object.keys(patch).length) onChange(patch);
  }, [reserva.id, provQ.data]);

  const set = (k, v) => onChange({ [k]: v });
  const gmapsUrl = (q) => q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : "";
  const meetingUrl = reserva.td_meeting_point_url || gmapsUrl(reserva.td_meeting_point || "");

  // Extraer campos automáticamente del sales_description (párrafo grande).
  // Comportamiento: **SOBRESCRIBE** los campos existentes con los extraídos
  // (petición Eduardo 2026-02: al reformatear un TD viejo hay que reemplazar
  // los valores antiguos). Los que no se detectan quedan intactos.
  const runExtractor = () => {
    const extracted = extractTDFieldsFromDescription(reserva.sales_description || "");
    const patch = {};
    let n = 0;
    Object.entries(extracted).forEach(([k, v]) => {
      if (v && v !== (reserva[k] || "")) { patch[k] = v; n += 1; }
    });
    if (n === 0) {
      toast.info("No se detectaron datos nuevos en la descripción.");
    } else {
      onChange(patch);
      toast.success(`Extraídos ${n} campo${n === 1 ? "" : "s"} desde la descripción · valores antiguos sobrescritos`);
    }
  };

  // Regenerar sales_description desde los campos estructurados (migración
  // del TD viejo al nuevo formato limpio). Al guardar, la siguiente vez que
  // se abra la reserva la descripción ya vendrá ordenada y solo habrá que
  // actualizar los datos variables (hora, small/private, etc.).
  const runReformat = () => {
    const html = composeTDDescriptionFromFields(reserva);
    if (!html.trim()) {
      toast.info("Antes de reformatear, rellena al menos un campo del TD.");
      return;
    }
    onChange({ sales_description: mergeTDDescription(html, reserva.sales_description) });
    toast.success("Descripción reformateada al nuevo formato · recuerda guardar la reserva");
  };

  return (
    <div className="rounded-lg border bg-card p-4" data-testid="td-fields-actividad">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-primary" />
          <h3 className="font-heading text-sm font-bold">
            Campos del Travel Document · {reserva.type === "transfer" ? "Transfer" : "Actividad"}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 gap-1 text-xs"
            onClick={runExtractor}
            data-testid="td-extract-btn"
            title="Analiza la descripción del template y separa automáticamente los campos vacíos (teléfono, meeting point, guía…)"
          >
            <Wand2 className="h-3 w-3" />Extraer de descripción
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 gap-1 border-emerald-400/60 bg-emerald-50/50 text-xs text-emerald-800 hover:bg-emerald-100"
            onClick={runReformat}
            data-testid="td-reformat-btn"
            title="Reescribe la descripción antigua usando los campos estructurados. Ideal para migrar TDs viejos al formato nuevo."
          >
            <RefreshCw className="h-3 w-3" />Guardar como formato nuevo
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {TD_ACTIVIDAD_FIELDS
          .filter((f) => !f.onlyType || f.onlyType === reserva.type)
          .filter((f) => !f.excludeType || f.excludeType !== reserva.type)
          .filter((f) => !f.excludeTypes || !f.excludeTypes.includes(reserva.type))
          .map((f) => {
          const value = reserva[f.key] || "";
          const empty = !f.optional && !value.toString().trim();
          const commonCls = `text-sm ${empty ? "border-rose-400 bg-rose-50/40 placeholder:text-rose-400" : ""}`;
          return (
            <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
              <label className={`mb-1 block text-[10px] font-semibold uppercase tracking-wider ${empty ? "text-rose-600" : "text-muted-foreground"}`}>
                {f.label} {empty && <span className="ml-0.5">*</span>}
              </label>
              {f.type === "textarea" ? (
                <textarea
                  className={`w-full rounded-md border px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${commonCls}`}
                  rows={f.key === "td_description" ? 3 : 2}
                  value={value}
                  onChange={(e) => set(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  data-testid={`td-field-${f.key}`}
                />
              ) : f.type === "select" ? (
                <select
                  className={`h-9 w-full rounded-md border bg-transparent px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${commonCls}`}
                  value={value}
                  onChange={(e) => set(f.key, e.target.value)}
                  data-testid={`td-field-${f.key}`}
                >
                  <option value="">— {f.placeholder} —</option>
                  {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <Input
                  value={value}
                  onChange={(e) => set(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className={`h-9 ${commonCls}`}
                  data-testid={`td-field-${f.key}`}
                />
              )}
              {f.key === "td_meeting_point" && value && (
                <div className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <MapPin className="h-3 w-3 text-blue-600" />
                  <a
                    href={meetingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 underline decoration-blue-400/50 underline-offset-2 hover:text-blue-900"
                    data-testid="td-meeting-gmaps-link"
                    title="Abre Google Maps con esta dirección en una pestaña nueva"
                  >
                    Abrir en Google Maps
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Selector de plantilla TD (Travel Document) por reserva.
// Es la descripción que se enviará a KimKim para generar los Travel Documents.
// value: { plantilla_id, plantilla_name, sales_description, sales_images }
// reserva: usada para sustituir placeholders {{hora_encuentro}}, {{num_pax}}...
// trip: idem — {{main_traveler}}, {{pais}}
export const ReservaTemplate = ({ reserva, trip, value, onChange, tripId, reservaId, kimkimUrl }) => {
  const qc = useQueryClient();
  const service = reserva?.service || "";
  const type = reserva?.type || "actividad";

  // Persistencia inmediata al backend cuando se aplica/quita/restaura una
  // plantilla. Antes exigía pulsar el botón «Guardar» principal — muchos
  // operadores lo olvidaban y en la pestaña Travel Document seguía "Sin
  // plantilla". 2026-C.6.
  const persistTemplate = async (patch, { silent = false } = {}) => {
    if (!tripId || !reservaId) return false;
    try {
      // Payload base con los 4 campos de plantilla siempre. Añadimos
      // `exclude_from_td` como campo opcional para poder toggearlo desde
      // el check "No incluir en el Travel Document" sin arrastrar el resto
      // del form (2026-08).
      const body = {
        plantilla_id: patch.plantilla_id ?? value?.plantilla_id ?? "",
        plantilla_name: patch.plantilla_name ?? value?.plantilla_name ?? "",
        sales_description: patch.sales_description ?? value?.sales_description ?? "",
        sales_images: patch.sales_images ?? value?.sales_images ?? [],
      };
      if (typeof patch.exclude_from_td === "boolean") {
        body.exclude_from_td = patch.exclude_from_td;
      }
      await api.put(`/trips/${tripId}/reservas/${reservaId}`, body);
      if (!silent) {
        toast.success(
          typeof patch.exclude_from_td === "boolean"
            ? (patch.exclude_from_td ? "Reserva excluida del Travel Document" : "Reserva incluida de nuevo en el Travel Document")
            : "Plantilla guardada en la reserva",
        );
      }
      // Invalida caches del trip para que TD tab y otras vistas se refresquen.
      qc.invalidateQueries({ queryKey: ["trip", tripId] });
      qc.invalidateQueries({ queryKey: ["td-compose", tripId] });
      qc.invalidateQueries({ queryKey: ["reserva-suggest", reservaId] });
      return true;
    } catch (e) {
      toast.error(apiErrorMessage(e));
      return false;
    }
  };

  // Snapshot inicial de la plantilla (lo que hay en el backend) — se usa para
  // detectar cambios locales sin guardar y mostrar un indicador visual.
  const [savedSnapshot, setSavedSnapshot] = useState({
    plantilla_id: value?.plantilla_id || "",
    plantilla_name: value?.plantilla_name || "",
    sales_description: value?.sales_description || "",
  });
  useEffect(() => {
    // Cuando el padre nos pasa una `value` nueva (p.ej. tras refetch del trip),
    // reseteamos el snapshot.
    setSavedSnapshot({
      plantilla_id: value?.plantilla_id || "",
      plantilla_name: value?.plantilla_name || "",
      sales_description: value?.sales_description || "",
    });
  }, [reservaId]);
  const hasUnsavedChanges =
    (value?.plantilla_id || "") !== savedSnapshot.plantilla_id ||
    (value?.plantilla_name || "") !== savedSnapshot.plantilla_name ||
    (value?.sales_description || "") !== savedSnapshot.sales_description;

  // Envoltorio: persistir + actualizar snapshot local.
  const persistAndSnapshot = async (patch, opts = {}) => {
    const ok = await persistTemplate(patch, opts);
    if (ok) {
      setSavedSnapshot({
        plantilla_id: patch.plantilla_id || "",
        plantilla_name: patch.plantilla_name || "",
        sales_description: patch.sales_description || "",
      });
    }
    return ok;
  };

  const [term, setTerm] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => { const t = setTimeout(() => setQ(term.trim()), 300); return () => clearTimeout(t); }, [term]);
  // Ciudad del filtro: por defecto la «Ciudad» de la reserva (Reservas, nov-2026); editable/borrable.
  const [cityFilter, setCityFilter] = useState(reserva.city || "");
  useEffect(() => { setCityFilter(reserva.city || ""); }, [reserva.id, reserva.city]);
  const cityQ = cityFilter.trim();

  // Vouchers analizados con IA que tengan `apply_plan.actions` disponibles.
  // Solo mostramos el CTA "Cargar información del voucher" si al menos un
  // voucher tiene datos nuevos que aplicar a la reserva/plantilla.
  const vouchersQ = useQuery({
    queryKey: ["vouchers", tripId, reservaId],
    queryFn: async () => (await api.get(`/trips/${tripId}/reservas/${reservaId}/vouchers`)).data,
    enabled: !!tripId && !!reservaId,
    staleTime: 15_000,
  });
  const voucherWithData = (vouchersQ.data?.vouchers || []).find((v) => {
    const a = v.ai_analysis;
    return a && a.status === "ok" && (
      (a.apply_plan?.actions?.length || 0) > 0 || (a.apply_plan?.conflicts?.length || 0) > 0
    );
  });

  const [voucherConflictModal, setVoucherConflictModal] = useState(null);
  const applyVoucherMut = useMutation({
    mutationFn: async ({ voucher, skip_keys }) => {
      // Auto-guardar plantilla si hay cambios sin guardar (evita perderlos al
      // refetchear tras el apply del backend).
      if (hasUnsavedChanges) {
        await persistAndSnapshot({
          plantilla_id: value?.plantilla_id || "",
          plantilla_name: value?.plantilla_name || "",
          sales_description: value?.sales_description || "",
          sales_images: value?.sales_images || [],
        }, { silent: true });
      }
      return (await api.post(
        `/trips/${tripId}/reservas/${reservaId}/vouchers/${voucher.id}/apply`,
        { skip_keys: skip_keys || [] },
      )).data;
    },
    onSuccess: (data) => {
      const n = (data.applied || []).length;
      if (n > 0) toast.success(`✓ ${n} campo${n > 1 ? "s" : ""} cargado${n > 1 ? "s" : ""} desde el voucher`);
      else toast.info("Nada nuevo que cargar (la reserva ya está al día)");
      // Propagar los updates al form del padre (ReservaDetail.form) para que
      // los cambios se vean inmediatamente en el editor de la plantilla, en
      // los campos date_end/hora, y en el editor de notas. El padre inicializa
      // `form` con `useState(() => ({...reserva}))`, así que un invalidate del
      // trip solo NO refresca su estado local — hay que empujarle los cambios
      // explícitamente vía `onChange` (que es un `merge` sobre `form`).
      const updates = data.reserva_updates || {};
      if (Object.keys(updates).length > 0) {
        onChange({ ...value, ...updates });
        // Actualizar snapshot para no falsos "cambios sin guardar"
        setSavedSnapshot({
          plantilla_id: value?.plantilla_id || "",
          plantilla_name: value?.plantilla_name || "",
          sales_description: updates.sales_description ?? value?.sales_description ?? "",
        });
      }
      // Refresca queries relacionadas por si otras vistas dependen del voucher.
      qc.invalidateQueries({ queryKey: ["trip", tripId] });
      qc.invalidateQueries({ queryKey: ["vouchers", tripId, reservaId] });
      setVoucherConflictModal(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo aplicar el voucher")),
  });

  const onLoadVoucher = () => {
    if (!voucherWithData) return;
    const conflicts = voucherWithData.ai_analysis?.apply_plan?.conflicts || [];
    if (conflicts.length > 0) {
      setVoucherConflictModal({
        voucher: voucherWithData,
        conflicts,
        actionsCount: voucherWithData.ai_analysis?.apply_plan?.actions?.length || 0,
      });
    } else {
      applyVoucherMut.mutate({ voucher: voucherWithData });
    }
  };

  // 1) Sugerencias inteligentes — llamada dedicada que devuelve score + quality.
  //    Ordenadas por relevancia (service_type + location "A → B" + cities + nombre).
  const suggestQ = useQuery({
    queryKey: ["reserva-suggest", reservaId],
    queryFn: async () => (await api.get(`/trips/reservas/${reservaId}/suggest-plantillas`, {
      params: { limit: 8, min_score: 0.25 },
    })).data,
    enabled: !!reservaId,
    staleTime: 60_000,
  });
  const suggestions = suggestQ.data?.suggestions || [];

  // 2) Buscador de texto libre — si el operador escribe algo, buscamos en toda
  //    la biblioteca de plantillas TD **sin filtrar por service_type**. Antes
  //    exigía coincidir con el tipo de la reserva; ahora la búsqueda libre es
  //    global (04-ago-2026). Esto rescata plantillas cuando la reserva está
  //    mal clasificada o no hay match automático.
  const searchQ = useQuery({
    queryKey: ["reserva-td-search", q, cityQ, reserva?.type],
    queryFn: async () => (await api.get("/plantillas", {
      // Nombre / proveedor (vía productos enlazados) + filtro duro por ciudad (si hay ciudad).
      // Sin texto: se listan las plantillas de la ciudad del mismo tipo que la reserva.
      params: {
        q, type: "TD", limit: 30,
        ...(cityQ ? { city: cityQ, city_strict: true } : {}),
        ...(!q && reserva.type ? { service_type: reserva.type } : {}),
      },
    })).data,
    enabled: q.length > 0 || cityQ.length > 0,
    keepPreviousData: true,
  });
  const searchItems = (searchQ.data?.items || []).map((p) => ({
    plantilla_id: p.id,
    name: p.name,
    service_type: p.service_type,
    location: p.location,
    cities: p.cities || [],
    provider_names: p.provider_names || [],
    quality: null,  // sin scoring en búsqueda libre
    score: null,
  }));

  // Lista efectiva: si hay término de búsqueda (o ciudad) → resultados de búsqueda;
  // si no → sugerencias inteligentes con badge de calidad.
  const searching = q.length > 0 || cityQ.length > 0;
  const items = searching ? searchItems : suggestions;
  const isLoading = searching ? searchQ.isLoading : suggestQ.isLoading;

  const pick = async (p) => {
    // La lista de sugerencias devuelve `plantilla_id`; la búsqueda devuelve
    // también `plantilla_id` normalizado arriba. Necesitamos description +
    // images completas, que la sugerencia no devuelve — pedimos el full doc.
    try {
      const { data } = await api.get(`/plantillas/${p.plantilla_id}`);
      // Usamos display_name si está disponible (contextualizado con la reserva).
      const chosenName = p.display_name || data.name;
      const patch = {
        plantilla_id: data.id, plantilla_name: chosenName,
        sales_description: data.description || "", sales_images: data.images || [],
      };
      onChange(patch);
      setEditing(true);
      await persistAndSnapshot(patch);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };
  const clear = async () => {
    const patch = { plantilla_id: "", plantilla_name: "", sales_description: "", sales_images: [] };
    onChange(patch);
    setEditing(false);
    await persistAndSnapshot(patch, { silent: true });
  };
  const refresh = async () => {
    if (!value?.plantilla_id) return;
    try {
      const { data } = await api.get(`/plantillas/${value.plantilla_id}`);
      const patch = { plantilla_id: data.id, plantilla_name: data.name, sales_description: data.description || "", sales_images: data.images || [] };
      onChange(patch);
      await persistAndSnapshot(patch, { silent: true });
      toast.success("Texto restaurado desde la plantilla");
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const selected = value?.plantilla_id;
  const [editing, setEditing] = useState(false);

  // Preview / edición previa antes de aceptar una plantilla ─── 2026-C.4
  // Al pulsar "Ver / Editar" en una fila de sugerencia, cargamos el HTML
  // completo. El usuario puede modificarlo y sólo se aplica al pulsar
  // "Usar esta versión". Sirve para revisar plantillas nuevas con placeholders
  // antes de asumirlas ciegamente.
  const [previewFor, setPreviewFor] = useState(null);   // { plantilla_id, name }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewDraft, setPreviewDraft] = useState({ description: "", images: [] });
  const usePreviewedVersion = async () => {
    if (!previewFor) return;
    const patch = {
      plantilla_id: previewFor.plantilla_id,
      plantilla_name: previewFor.display_name || previewFor.name,
      sales_description: previewDraft.description,
      sales_images: previewDraft.images,
    };
    onChange(patch);
    setEditing(false);
    setPreviewFor(null);
    await persistAndSnapshot(patch);
  };
  const openPreview = async (p) => {
    setPreviewFor({ plantilla_id: p.plantilla_id, name: p.name, display_name: p.display_name });
    setPreviewLoading(true);
    setPreviewDraft({ description: "", images: [] });
    try {
      const { data } = await api.get(`/plantillas/${p.plantilla_id}`);
      setPreviewDraft({ description: data.description || "", images: data.images || [] });
    } catch (e) {
      toast.error(apiErrorMessage(e));
      setPreviewFor(null);
    } finally {
      setPreviewLoading(false);
    }
  };
  const desc = value?.sales_description || "";
  const hasDoc = !!(value?.plantilla_id || (desc && desc.trim()));
  const setDesc = (txt) => onChange({ ...value, plantilla_name: value?.plantilla_name || "Texto libre", sales_description: txt });
  const startFree = () => { onChange({ plantilla_id: "", plantilla_name: "Texto libre", sales_description: desc, sales_images: value?.sales_images || [] }); setEditing(true); };

  const [kkInfo, setKkInfo] = useState(null);
  // «Guardar como nueva plantilla en catálogo» ── modal de previsualización.
  // El operador puede ajustar nombre/descripción antes de validar. Al aceptar
  // se hace POST /plantillas con type=TD y service_type=type actual.
  const [catalogModal, setCatalogModal] = useState(null); // { name, description, images, service_type, location, cities }
  const openCatalogModal = () => {
    const suggestedName = value?.plantilla_name && value.plantilla_name !== "Texto libre"
      ? value.plantilla_name
      : (reserva.service || "").trim();
    setCatalogModal({
      name: suggestedName,
      description: value?.sales_description || "",
      images: value?.sales_images || [],
      service_type: type,
      location: reserva.city || "",
      cities: reserva.city ? [reserva.city] : [],
      event_type: reserva.type === "alojamiento" ? "Accommodations" : "Actividades",
    });
  };
  const saveCatalogMut = useMutation({
    mutationFn: async () => {
      if (!catalogModal?.name?.trim()) throw new Error("El nombre no puede estar vacío");
      if (!catalogModal?.description?.trim()) throw new Error("La descripción no puede estar vacía");
      return api.post("/plantillas", {
        name: catalogModal.name.trim(),
        description: catalogModal.description,
        images: catalogModal.images || [],
        type: "TD",
        service_type: catalogModal.service_type,
        location: catalogModal.location,
        cities: catalogModal.cities,
        event_type: catalogModal.event_type,
      });
    },
    onSuccess: (res) => {
      toast.success(`Plantilla «${catalogModal.name}» guardada en el catálogo`);
      setCatalogModal(null);
      // Enlazamos la reserva a la plantilla recién creada (fuente única).
      if (res?.data?.id) {
        const patch = {
          plantilla_id: res.data.id,
          plantilla_name: res.data.name,
          sales_description: res.data.description || catalogModal.description,
          sales_images: res.data.images || catalogModal.images || [],
        };
        onChange(patch);
        persistAndSnapshot(patch, { silent: true });
      }
      qc.invalidateQueries({ queryKey: ["plantillas"] });
      qc.invalidateQueries({ queryKey: ["reserva-suggest", reservaId] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const kkMut = useMutation({
    mutationFn: async (dry) => {
      // Auto-guardar la plantilla ANTES de simular/enviar: si el agente editó
      // el texto libremente y no pulsó «Guardar cambios», el backend seguiría
      // con la versión antigua y enviaría texto obsoleto a KimKim.
      if (hasUnsavedChanges) {
        const patch = {
          plantilla_id: value?.plantilla_id || "",
          plantilla_name: value?.plantilla_name || "",
          sales_description: value?.sales_description || "",
          sales_images: value?.sales_images || [],
        };
        const ok = await persistAndSnapshot(patch, { silent: true });
        if (!ok) throw new Error("No se pudo guardar la plantilla antes de enviar");
      }
      return api.post(`/trips/${tripId}/reservas/${reservaId}/kimkim/push`, { dry_run: dry });
    },
    onSuccess: (res, dry) => {
      const m = res.data.match || {};
      const isEdit = !!m.is_edit;
      const verbDry = isEdit ? "Actualizar" : "Enviar";
      const verbOk = isEdit ? "Actualizado" : "Enviado";
      if (dry) {
        setKkInfo({ type: "dry", text: `Listo para ${verbDry.toLowerCase()} · match ${m.confidence || "?"} con «${m.kimkim_title || ""}». Nº conf: ${res.data.would_send?.["addon_booking[confirmation_number]"] || "—"}` });
        toast.success(`Simulación OK (${isEdit ? "se actualizará la confirmación existente" : "no se ha enviado nada"})`);
      } else {
        setKkInfo({ type: "ok", text: `${verbOk} en KimKim (match ${m.confidence || "?"} con «${m.kimkim_title || ""}»)` });
        toast.success(`${verbOk} en KimKim`);
      }
    },
    onError: (e) => { setKkInfo({ type: "err", text: apiErrorMessage(e) }); toast.error(apiErrorMessage(e)); },
  });

  return (
    <div className="space-y-4" data-testid="reserva-template">
      <div className="flex items-start gap-2 rounded-md border border-dashed bg-accent/30 p-3 text-xs text-muted-foreground">
        <LayoutTemplate className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <p>Plantilla <strong>TD (Travel Document)</strong> de esta reserva. Sugerimos una por el nombre del servicio;
        puedes aceptarla, elegir otra o <strong>editar el texto libremente</strong> antes de enviarlo (nombre del guía, hora de comienzo, teléfono de contacto…). Esta descripción es la que se envía a <strong>KimKim</strong>.</p>
      </div>

      {/* ─── Campos estructurados del Travel Document (2026-02) ─────────────
          Datos que se inyectan en el TD final. Autopoblación desde la
          reserva; los que quedan vacíos aparecen en rojo.
          Actividades y transfers comparten estructura de campos. */}
      {(type === "actividad" || type === "transfer") && (
        <TDFieldsActividad reserva={reserva} trip={trip} onChange={onChange} />
      )}
      {type === "alojamiento" && (
        <TDFieldsAlojamiento reserva={reserva} trip={trip} onChange={onChange} />
      )}

      {/* CTA: cargar información del voucher del proveedor a la plantilla + reserva */}
      {voucherWithData && (
        <VoucherLoadCTA
          voucher={voucherWithData}
          busy={applyVoucherMut.isPending}
          onLoad={onLoadVoucher}
        />
      )}

      {/* Plantilla seleccionada / Texto del documento */}
      {hasDoc || editing ? (
        <div className="rounded-lg border bg-card p-4" data-testid="reserva-template-selected">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {value?.plantilla_id
                ? <Badge className="shrink-0 gap-1"><Check className="h-3 w-3" /> Plantilla TD</Badge>
                : <Badge variant="secondary" className="shrink-0 gap-1"><Pencil className="h-3 w-3" /> Texto libre</Badge>}
              <Input
                value={value?.plantilla_name || ""}
                onChange={(e) => onChange({ ...value, plantilla_name: e.target.value })}
                placeholder="Título de la plantilla (editable)"
                className="h-8 min-w-0 flex-1 border-transparent bg-transparent px-1.5 font-semibold shadow-none hover:border-input focus-visible:border-input focus-visible:ring-1"
                data-testid="reserva-template-name"
                title="Puedes personalizar el título; se envía tal cual a KimKim"
              />
            </div>
            <div className="flex items-center gap-1">
              <Button type="button" variant={editing ? "secondary" : "ghost"} size="sm" onClick={() => setEditing((e) => !e)} data-testid="reserva-template-edit-toggle">
                {editing ? <><Eye className="mr-1 h-3.5 w-3.5" />Vista previa</> : <><Pencil className="mr-1 h-3.5 w-3.5" />Editar texto</>}
              </Button>
              {value?.plantilla_id && <Button type="button" variant="ghost" size="sm" onClick={refresh} data-testid="reserva-template-refresh"><RefreshCw className="mr-1 h-3.5 w-3.5" />Restaurar plantilla</Button>}
              <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={clear} data-testid="reserva-template-clear"><X className="mr-1 h-3.5 w-3.5" />Quitar</Button>
            </div>
          </div>
          {value?.sales_images?.length > 0 && (
            <div className="mb-3 flex gap-2 overflow-x-auto">
              {value.sales_images.slice(0, 5).map((src, i) => (
                <img key={i} src={src} alt="" className="h-16 w-24 shrink-0 rounded object-cover" />
              ))}
            </div>
          )}
          {editing ? (
            <div className="space-y-1.5">
              <RichTextEditor
                value={desc}
                onChange={setDesc}
                placeholder="Escribe el texto del Travel Document. Usa 'Insertar dato' para los campos de la reserva."
                minRows={10}
                testId="reserva-template-editor"
              />
              <p className="text-[11px] text-muted-foreground">
                <span className="font-mono text-primary">{`{{campos}}`}</span> se sustituyen con datos de la reserva (hora, pax, ciudad…) al publicar el TD.
                {value?.plantilla_id ? " Editado manualmente; «Restaurar plantilla» recupera el texto original." : ""}
              </p>
            </div>
          ) : (
            <div
              className="prose prose-sm max-w-none text-sm text-foreground [&_a]:text-primary [&_a]:underline"
              data-testid="reserva-template-desc"
              dangerouslySetInnerHTML={{ __html: fillPlaceholders(desc, buildContext({ reserva, trip })) || "<em>Sin descripción. Pulsa «Editar texto» para escribir.</em>" }}
            />
          )}
        </div>
      ) : (
        <div className="flex flex-col items-start gap-2 rounded-lg border border-dashed p-4" data-testid="reserva-template-empty">
          <p className="text-sm text-muted-foreground">Sin plantilla asignada. Elige una de las sugerencias de abajo o escribe el texto libremente.</p>
          <Button type="button" variant="outline" size="sm" onClick={startFree} data-testid="reserva-template-freewrite"><Pencil className="mr-1.5 h-3.5 w-3.5" />Escribir texto libre</Button>
        </div>
      )}

      {/* Buscador + sugerencias */}
      <div className="space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Buscar plantilla TD por nombre o proveedor… Deja vacío para ver las de la ciudad."
              className="pl-8"
              data-testid="reserva-template-search"
            />
          </div>
          <div className="relative sm:w-56">
            <MapPin className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              placeholder="Ciudad (todas)"
              title="Filtra por ciudad. Por defecto, la ciudad de la reserva; bórrala para buscar en todas."
              className="pl-8 pr-7"
              data-testid="reserva-template-city"
            />
            {cityFilter && (
              <button type="button" onClick={() => setCityFilter("")} className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground" title="Quitar filtro de ciudad" data-testid="reserva-template-city-clear">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        <div className="max-h-72 space-y-1.5 overflow-auto rounded-lg border p-1.5" data-testid="reserva-template-list">
          {isLoading ? (
            <p className="p-3 text-center text-sm text-muted-foreground">Buscando plantillas…</p>
          ) : items.length === 0 ? (
            <p className="p-3 text-center text-sm text-muted-foreground">
              {q
                ? `Sin coincidencias para «${q}»${cityQ ? ` en ${cityQ}` : " en toda la biblioteca TD"}. Prueba otro término${cityQ ? " o quita la ciudad" : ""}.`
                : cityQ
                  ? `Sin plantillas de este tipo en «${cityQ}». Escribe un nombre o proveedor, o quita la ciudad para ver sugerencias.`
                  : "Sin sugerencias para esta reserva. Escribe arriba para buscar en toda la biblioteca."}
            </p>
          ) : (
            items.map((p, i) => {
              const isPicked = value?.plantilla_id === p.plantilla_id;
              const qm = p.quality ? QUALITY_META[p.quality] : null;
              const QIcon = qm?.icon;
              return (
                <div key={p.plantilla_id} className="flex items-center gap-2 rounded-md p-2 hover:bg-accent" data-testid={`reserva-template-option-${i}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {qm && (
                        <Badge className={`gap-1 border text-[10px] ${qm.cls}`} data-testid={`reserva-template-quality-${p.quality}-${i}`}>
                          {QIcon && <QIcon className="h-3 w-3" />}{qm.label}
                        </Badge>
                      )}
                      <span className="truncate text-sm font-medium" title={p.name}>{p.display_name || p.name}</span>
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                      {p.service_type && <span>{p.service_type}</span>}
                      {p.location && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3 w-3" />{p.location}
                        </span>
                      )}
                      {(p.provider_names || []).length > 0 && (
                        <span className="inline-flex items-center gap-1" title="Proveedor (vía producto enlazado)" data-testid={`reserva-template-provider-${i}`}>
                          <Building2 className="h-3 w-3" />{p.provider_names.join(" · ")}
                        </span>
                      )}
                      {p.score != null && (
                        <span className="tabular-nums opacity-60">score {p.score.toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => openPreview(p)}
                    data-testid={`reserva-template-preview-${i}`}
                    title="Ver / editar el texto de la plantilla antes de aplicarla"
                  >
                    <Eye className="mr-1 h-3.5 w-3.5" />Ver
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={isPicked ? "secondary" : "outline"}
                    onClick={() => pick(p)}
                    data-testid={`reserva-template-use-${i}`}
                  >
                    {isPicked ? "Elegida" : "Usar"}
                  </Button>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Guardar cambios de esta reserva · dos acciones diferenciadas */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-card p-4" data-testid="reserva-template-save-bar">
        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-sm font-semibold flex items-center gap-2">
            Guardar Template
            {hasUnsavedChanges && (
              <span
                data-testid="reserva-template-unsaved-badge"
                className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700"
              >
                <Pencil className="h-2.5 w-2.5" /> cambios sin guardar
              </span>
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            <strong>Guardar Template</strong>: aplica los cambios (campos + descripción) sólo a esta reserva y actualiza el Travel Document.
            {" "}<strong>Guardar como Nueva Plantilla</strong>: además guarda una copia en el catálogo TD reutilizable, previo repaso.
          </p>
          {/* Check para excluir esta reserva del TD público + push a KimKim.
              Uso: reservas internas/operativas que no debe ver el viajero.
              Persiste al vuelo (no requiere pulsar Guardar) porque es un
              toggle atómico sin edición intermedia. */}
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-rose-300/70 bg-rose-50/40 px-2.5 py-1 text-xs text-rose-800 hover:bg-rose-50">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-rose-600"
              checked={!!reserva.exclude_from_td}
              onChange={(e) => {
                const checked = e.target.checked;
                onChange({ exclude_from_td: checked });
                persistAndSnapshot({ exclude_from_td: checked }, { silent: false });
              }}
              data-testid="reserva-exclude-from-td"
            />
            <span><strong>No incluir esta reserva en el Travel Document</strong> · también se bloquea el envío a KimKim</span>
          </label>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openCatalogModal}
            data-testid="reserva-template-save-as-new"
            title="Guarda esta descripción como una plantilla TD nueva en el catálogo (para reutilizar en otras reservas)"
          >
            <Plus className="mr-1.5 h-4 w-4" />Guardar como Nueva Plantilla
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!hasUnsavedChanges}
            onClick={() => {
              const patch = {
                plantilla_id: value?.plantilla_id || "",
                plantilla_name: value?.plantilla_name || "",
                sales_description: value?.sales_description || "",
                sales_images: value?.sales_images || [],
              };
              persistAndSnapshot(patch);
            }}
            data-testid="reserva-template-save"
          >
            <Check className="mr-1.5 h-4 w-4" />Guardar Template
          </Button>
        </div>
      </div>

      {/* Enviar esta reserva a KimKim */}
      <div className="space-y-2 rounded-lg border bg-card p-4" data-testid="reserva-kimkim-push">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold flex items-center gap-2">
              Enviar a KimKim
              {hasUnsavedChanges && (
                <span
                  data-testid="reserva-kimkim-unsaved-badge"
                  className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700"
                  title="Los cambios de la plantilla se guardarán automáticamente al pulsar Simular o Enviar"
                >
                  <Pencil className="h-2.5 w-2.5" /> cambios sin guardar
                </span>
              )}
            </p>
            <p className="text-xs text-muted-foreground">
              Empuja la confirmación (Travel Document) de esta reserva a KimKim.
              {" "}Si el item ya está confirmado allí, se <strong>actualiza</strong> el texto (no se duplica).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" disabled={!kimkimUrl || kkMut.isPending} onClick={() => kkMut.mutate(true)} data-testid="reserva-kimkim-simulate">
              {kkMut.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FlaskConical className="mr-1.5 h-4 w-4" />}Simular
            </Button>
            <Button type="button" size="sm" disabled={!kimkimUrl || kkMut.isPending} onClick={() => kkMut.mutate(false)} data-testid="reserva-kimkim-send">
              <Send className="mr-1.5 h-4 w-4" />Enviar a KimKim
            </Button>
          </div>
        </div>
        {!kimkimUrl && <p className="text-xs text-amber-600">Configura primero la URL de KimKim en la pestaña «KimKim» del viaje.</p>}
        {kkInfo && <p className={`text-xs ${kkInfo.type === "err" ? "text-rose-600" : kkInfo.type === "ok" ? "text-emerald-600" : "text-muted-foreground"}`} data-testid="reserva-kimkim-result">{kkInfo.text}</p>}
      </div>

      {/* Modal de preview / edición previa de una plantilla (2026-C.4) */}
      <Dialog open={!!previewFor} onOpenChange={(o) => { if (!o) setPreviewFor(null); }}>
        <DialogContent className="max-w-3xl" data-testid="reserva-template-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-primary" />
              Ver / editar plantilla antes de aplicar
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              Estás previsualizando <strong>{previewFor?.name}</strong>. Puedes ajustar el texto
              (por ejemplo rellenar los placeholders <code className="rounded bg-muted px-1">{`{{start_location}}`}</code>/<code className="rounded bg-muted px-1">{`{{end_location}}`}</code>)
              y sólo se aplicará a esta reserva cuando pulses «Usar esta versión».
            </p>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex h-40 items-center justify-center" data-testid="reserva-template-preview-loading">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-3">
              {previewDraft.images?.length > 0 && (
                <div className="flex gap-2 overflow-x-auto">
                  {previewDraft.images.slice(0, 5).map((src, i) => (
                    <img key={i} src={src} alt="" className="h-16 w-24 shrink-0 rounded object-cover" />
                  ))}
                </div>
              )}
              <RichTextEditor
                value={previewDraft.description}
                onChange={(v) => setPreviewDraft((d) => ({ ...d, description: v }))}
                placeholder="Contenido de la plantilla"
                minRows={12}
                testId="reserva-template-preview-editor"
              />
              <p className="text-[11px] text-muted-foreground">
                La plantilla original en la biblioteca no cambia; sólo estás editando el texto
                de <em>esta reserva</em>.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPreviewFor(null)} data-testid="reserva-template-preview-cancel">
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={usePreviewedVersion}
              disabled={previewLoading || !previewDraft.description?.trim()}
              data-testid="reserva-template-preview-use"
            >
              <Check className="mr-1 h-4 w-4" />Usar esta versión
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal «Guardar como Nueva Plantilla» — preview + validación (04-ago-2026)
          Muestra los datos que se van a guardar en el catálogo TD:
            · Nombre (editable, se pre-rellena con el título de la reserva)
            · Descripción HTML completa (editable)
            · Service type (readonly, viene del type de la reserva)
            · Location + cities (editables)
          Solo tras revisar y pulsar «Validar y guardar» se hace POST /plantillas. */}
      <Dialog open={!!catalogModal} onOpenChange={(o) => { if (!o) setCatalogModal(null); }}>
        <DialogContent className="max-w-3xl" data-testid="reserva-template-catalog-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-primary" />
              Guardar como Nueva Plantilla en el Catálogo
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              Revisa el contenido antes de añadirlo al catálogo TD. Estará disponible como sugerencia en cualquier reserva futura del mismo tipo.
            </p>
          </DialogHeader>
          {catalogModal && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Nombre de la plantilla *</label>
                  <Input
                    value={catalogModal.name}
                    onChange={(e) => setCatalogModal((s) => ({ ...s, name: e.target.value }))}
                    placeholder="Ej. Private Transfer Lisbon Airport to Porto Bay"
                    data-testid="reserva-template-catalog-name"
                    className="h-9"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Service type</label>
                  <Input value={catalogModal.service_type || "—"} readOnly className="h-9 bg-muted/40" data-testid="reserva-template-catalog-service-type" />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ciudad principal</label>
                  <Input
                    value={catalogModal.location}
                    onChange={(e) => setCatalogModal((s) => ({ ...s, location: e.target.value, cities: e.target.value ? [e.target.value] : [] }))}
                    placeholder="Ej. Lisbon"
                    data-testid="reserva-template-catalog-location"
                    className="h-9"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Descripción HTML *</label>
                <RichTextEditor
                  value={catalogModal.description}
                  onChange={(v) => setCatalogModal((s) => ({ ...s, description: v }))}
                  placeholder="Contenido de la plantilla"
                  minRows={10}
                  testId="reserva-template-catalog-editor"
                />
              </div>
              {catalogModal.images?.length > 0 && (
                <div>
                  <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Imágenes ({catalogModal.images.length})</label>
                  <div className="flex gap-2 overflow-x-auto">
                    {catalogModal.images.slice(0, 8).map((src, i) => (
                      <img key={i} src={src} alt="" className="h-16 w-24 shrink-0 rounded object-cover" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCatalogModal(null)} data-testid="reserva-template-catalog-cancel">
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => saveCatalogMut.mutate()}
              disabled={saveCatalogMut.isPending || !catalogModal?.name?.trim() || !catalogModal?.description?.trim()}
              data-testid="reserva-template-catalog-save"
            >
              {saveCatalogMut.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />}
              Validar y guardar en catálogo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de discrepancias al cargar el voucher */}
      {voucherConflictModal && (
        <VoucherConflictModal
          data={voucherConflictModal}
          busy={applyVoucherMut.isPending}
          onCancel={() => setVoucherConflictModal(null)}
          onApplyOthers={() =>
            applyVoucherMut.mutate({
              voucher: voucherConflictModal.voucher,
              skip_keys: voucherConflictModal.conflicts.map((c) => c.key),
            })
          }
        />
      )}
    </div>
  );
};


// -----------------------------------------------------------------------------
// CTA "Cargar información del voucher" — solo aparece si algún voucher tiene
// datos nuevos que se puedan meter en la reserva/plantilla. Encaja debajo del
// hint de "Plantilla TD" y encima del cuadro de la plantilla actual.
function VoucherLoadCTA({ voucher, busy, onLoad }) {
  const plan = voucher.ai_analysis?.apply_plan || {};
  const nActions = plan.actions?.length || 0;
  const nConflicts = plan.conflicts?.length || 0;
  const appliedAt = voucher.ai_analysis?.applied_at;

  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-lg border border-violet-200 bg-gradient-to-r from-violet-50 to-fuchsia-50 p-3"
      data-testid="reserva-template-voucher-cta"
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-100">
        <Sparkles className="h-4 w-4 text-violet-600" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-violet-900">
          {appliedAt ? "Voucher ya cargado — puedes re-cargar si el proveedor lo actualizó" : "El voucher del proveedor tiene datos para esta plantilla"}
        </p>
        <p className="mt-0.5 text-xs text-violet-700/80">
          <span className="font-mono">{voucher.filename}</span> ·{" "}
          {nActions > 0 && (
            <>{nActions} campo{nActions > 1 ? "s" : ""} listo{nActions > 1 ? "s" : ""} para cargar</>
          )}
          {nActions > 0 && nConflicts > 0 && " · "}
          {nConflicts > 0 && (
            <span className="font-semibold text-red-700">
              ⚠ {nConflicts} discrepancia{nConflicts > 1 ? "s" : ""}
            </span>
          )}
          {appliedAt && ` · aplicado ${new Date(appliedAt).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}`}
        </p>
      </div>
      <Button
        size="sm"
        onClick={onLoad}
        disabled={busy}
        className="shrink-0 rounded-full bg-violet-600 hover:bg-violet-700"
        data-testid="reserva-template-voucher-load"
      >
        {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
        {appliedAt ? "Re-cargar" : "Cargar información del voucher"}
      </Button>
    </div>
  );
}


// Modal de discrepancias reusable. Muestra las diferencias con máxima
// visibilidad (grid 2 columnas SOFI vs Voucher, tarjetas con ring rojo) y
// ofrece «Cancelar» o «Aplicar solo los campos sin conflicto».
function VoucherConflictModal({ data, busy, onCancel, onApplyOthers }) {
  const { conflicts, actionsCount } = data;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-900">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100">⚠</span>
            {conflicts.length} discrepancia{conflicts.length > 1 ? "s" : ""} entre voucher y SOFI
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          El voucher del proveedor no coincide con los datos guardados en SOFI. <strong>Ningún dato en conflicto se sobrescribirá.</strong> Decide qué hacer para cada uno:
        </p>
        <div className="my-2 max-h-[45vh] space-y-2 overflow-y-auto">
          {conflicts.map((c) => (
            <div key={c.key} className="rounded-lg border-2 border-red-300 bg-red-50 p-3" data-testid={`voucher-conflict-item-${c.key}`}>
              <p className="text-xs font-bold uppercase tracking-wide text-red-800">{c.display}</p>
              <div className="mt-1.5 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded bg-white p-2">
                  <p className="text-[10px] font-semibold text-muted-foreground">EN SOFI</p>
                  <p className="mt-0.5 font-mono text-sm">{c.sofi_value || "—"}</p>
                </div>
                <div className="rounded bg-white p-2 ring-2 ring-red-300">
                  <p className="text-[10px] font-semibold text-red-700">EN EL VOUCHER</p>
                  <p className="mt-0.5 font-mono text-sm">{c.voucher_value || "—"}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-700">
          <p className="font-semibold">¿Qué hacer?</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            <li>Si el <strong>voucher es incorrecto</strong> → contacta al proveedor y pide uno corregido.</li>
            <li>Si <strong>SOFI está desactualizado</strong> → corrige los datos en la reserva antes de aplicar.</li>
            <li>O aplica los otros campos ahora y resuelves las discrepancias después.</li>
          </ul>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} data-testid="voucher-conflict-cancel">Cancelar</Button>
          <Button
            onClick={onApplyOthers}
            disabled={busy || actionsCount === 0}
            data-testid="voucher-conflict-apply-others"
          >
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Aplicar solo los campos sin conflicto ({actionsCount})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
